import { useState } from 'react'
import { useForm } from '@inertiajs/react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { TimeEntryForm } from './TimeEntryForm'
import type { ActivitySubtype, ActivityType, TimeEntryFormData } from '@/types'

interface Props {
  activitySubtypes: ActivitySubtype[]
  activityTypes: ActivityType[]
  defaultDate?: string
  trigger?: React.ReactNode
  onSuccess?: () => void
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

export function QuickAddModal({
  activitySubtypes,
  activityTypes,
  defaultDate,
  trigger,
  onSuccess,
  open: controlledOpen,
  onOpenChange,
}: Props) {
  const { t } = useTranslation()
  const [internalOpen, setInternalOpen] = useState(false)

  // Support both controlled and uncontrolled modes
  const isControlled = controlledOpen !== undefined
  const open = isControlled ? controlledOpen : internalOpen
  const setOpen = isControlled ? (onOpenChange ?? (() => {})) : setInternalOpen

  const { data, setData, post, processing, errors, reset } = useForm<TimeEntryFormData>({
    activity_subtype_id: null,
    activity_type_id: null,
    date: defaultDate || new Date().toISOString().split('T')[0],
    started_at: null,
    ended_at: null,
    duration_minutes: null,
    notes: null,
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    post('/time_entries', {
      preserveScroll: true,
      onSuccess: () => {
        reset()
        setOpen(false)
        onSuccess?.()
      },
    })
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || <Button>{t('timeEntries.addEntry')}</Button>}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('timeEntries.addEntry')}</DialogTitle>
          <DialogDescription>{t('timeEntries.addEntryDescription')}</DialogDescription>
        </DialogHeader>

        <TimeEntryForm
          activitySubtypes={activitySubtypes}
          activityTypes={activityTypes}
          data={data}
          setData={setData}
          errors={errors}
          processing={processing}
          onSubmit={handleSubmit}
          onCancel={() => setOpen(false)}
          autoFocusActivity
        />
      </DialogContent>
    </Dialog>
  )
}
